"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BlueSphereMaterial = void 0;
const three_21 = require("three");
//
const loader = new three_21.TextureLoader();
let noiseTexture = loader.load('/resources/textures/perlinNoise.png');
class BlueSphereMaterial extends three_21.ShaderMaterial {
    constructor() {
        super();
        this.vertexShader = `
        uniform sampler2D uNoiseTexture;
        uniform float uTime;

        varying vec2 vUv;
        varying vec3 vNormal;
        varying vec3 vFe;
        varying float vDistortion;

        //
        vec3 mod289(vec3 x)
        {
            return x - floor(x * (1.0 / 289.0)) * 289.0;
        }

        vec4 mod289(vec4 x)
        {
            return x - floor(x * (1.0 / 289.0)) * 289.0;
        }

        vec4 permute(vec4 x)
        {
            return mod289(((x*34.0)+1.0)*x);
        }

        vec4 taylorInvSqrt(vec4 r)
        {
            return 1.79284291400159 - 0.85373472095314 * r;
        }

        vec3 fade(vec3 t) {
            return t*t*t*(t*(t*6.0-15.0)+10.0);
        }

        // Classic Perlin noise, periodic variant
        float pnoise(vec3 P, vec3 rep)
        {
            vec3 Pi0 = mod(floor(P), rep); // Integer part, modulo period
            vec3 Pi1 = mod(Pi0 + vec3(1.0), rep); // Integer part + 1, mod period
            Pi0 = mod289(Pi0);
            Pi1 = mod289(Pi1);
            vec3 Pf0 = fract(P); // Fractional part for interpolation
            vec3 Pf1 = Pf0 - vec3(1.0); // Fractional part - 1.0
            vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
            vec4 iy = vec4(Pi0.yy, Pi1.yy);
            vec4 iz0 = Pi0.zzzz;
            vec4 iz1 = Pi1.zzzz;

            vec4 ixy = permute(permute(ix) + iy);
            vec4 ixy0 = permute(ixy + iz0);
            vec4 ixy1 = permute(ixy + iz1);

            vec4 gx0 = ixy0 * (1.0 / 7.0);
            vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5;
            gx0 = fract(gx0);
            vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
            vec4 sz0 = step(gz0, vec4(0.0));
            gx0 -= sz0 * (step(0.0, gx0) - 0.5);
            gy0 -= sz0 * (step(0.0, gy0) - 0.5);

            vec4 gx1 = ixy1 * (1.0 / 7.0);
            vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5;
            gx1 = fract(gx1);
            vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
            vec4 sz1 = step(gz1, vec4(0.0));
            gx1 -= sz1 * (step(0.0, gx1) - 0.5);
            gy1 -= sz1 * (step(0.0, gy1) - 0.5);

            vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
            vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
            vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
            vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
            vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
            vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
            vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
            vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);

            vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
            g000 *= norm0.x;
            g010 *= norm0.y;
            g100 *= norm0.z;
            g110 *= norm0.w;
            vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
            g001 *= norm1.x;
            g011 *= norm1.y;
            g101 *= norm1.z;
            g111 *= norm1.w;

            float n000 = dot(g000, Pf0);
            float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
            float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
            float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
            float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
            float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
            float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
            float n111 = dot(g111, Pf1);

            vec3 fade_xyz = fade(Pf0);
            vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
            vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
            float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x);
            return 2.2 * n_xyz;
        }
        //

        void main() {

            vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
            vec3 fE = ( mvPosition * modelMatrix * vec4( position, 1.0 ) ).xyz;

            float distortion = pnoise( ( normal * 1.98 + uTime * 0.05 ) * 1.0, vec3( 100.0 ) ) * 0.3;

            vec3 newPosition = position + ( normal * distortion );

            gl_Position = projectionMatrix * modelViewMatrix * vec4( newPosition, 1.0 );

            vUv = uv;
            vNormal = normal;
            vFe = fE;
            vDistortion = distortion;

        }
        `,
            this.fragmentShader = `

        uniform float uTime;
        uniform vec3 uColorRed;
        uniform vec3 uColorDarkRed;
        uniform vec3 uDirection;
        uniform vec3 uLightColor;
        uniform sampler2D uNoiseTexture;

        varying vec2 vUv;
        varying vec3 vNormal;
        varying vec3 vFe;
        varying float vDistortion;

        void main() {

            // Diffuse
            vec3 norm = normalize( vNormal );
            float diff = max( dot( norm, uDirection ), 0.0 );
            vec3 diffuse = uLightColor * diff;

            // Specular light
            float shininess = 3.0;
            vec3 H = normalize( uDirection + vFe );
            float specular_intensity = pow( max( dot( norm, uDirection + vFe ), 0.0 ), shininess );
            vec4 specular_color = vec4( uLightColor, 1.0 );
            specular_color = specular_intensity * specular_color;

            vec4 txtNoise1 = texture2D( uNoiseTexture, vec2( vUv.x + sin( uTime * 0.3 ) * 0.05 + cos( uTime * 0.3 ) * 0.05, vUv.y - sin( uTime * 0.34 ) * 0.05 + cos( uTime * 0.3 ) * 0.05 ) );
            vec4 txtNoise2 = texture2D( uNoiseTexture, vec2( vUv.x - sin( uTime * 0.07 ) * 0.05 + cos( uTime * 0.07 ) * 0.05, vUv.y + sin( uTime * 0.077 ) * 0.05 + cos( uTime * 0.07 ) * 0.05 + 0.2 ) );

            // gl_FragColor = vec4( txtNoise1 * 0.7 + txtNoise2 * 0.7 );
            gl_FragColor = vec4( vec3( - vDistortion * 10.0 ), 1.0 );

            gl_FragColor.rgb = mix( gl_FragColor.rgb, uColorDarkRed, txtNoise2.rgb * 7.0 );
            gl_FragColor.rgb = mix( uColorRed, gl_FragColor.rgb, txtNoise1.rgb * 1.0 );

            // gl_FragColor.a = ( txtNoise1.r + txtNoise2.r ) * 0.5;
            // gl_FragColor.a = smoothstep( 0.4, 1.0, txtNoise1.r ) * 2.1;

            gl_FragColor.rgb = mix(  gl_FragColor.rgb, uLightColor, txtNoise1.rgb ) * 0.9;

        }
        `,
            this.uniforms = {
                uTime: { value: 0.0 },
                uColorRed: { value: new three_21.Color(0x0a30ad) },
                uColorDarkRed: { value: new three_21.Color(0x0a1363) },
                uDirection: { value: new three_21.Vector3(-0.1, -0.10, -0.2) },
                uLightColor: { value: new three_21.Color(0xcfdfe8) },
                uNoiseTexture: { value: noiseTexture }
            };
    }
}
exports.BlueSphereMaterial = BlueSphereMaterial;
;
